import React from 'react';
import { Button } from './ui/button';
import { ChevronRight, Truck, Flame } from 'lucide-react';

export const HeroSectionDark = ({ onOrderClick }) => {
  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 bg-black">
      {/* Blurred fries background */}
      <div 
        className="fries-bg-blur" 
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1630384060421-cb20d0e0649d)'
        }}
      ></div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-950/30"></div>
      
      {/* Animated background elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-red-600/20 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
      <div className="absolute top-40 right-10 w-72 h-72 bg-orange-600/20 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-red-700/20 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left space-y-6">
            <div className="inline-block">
              <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg shadow-red-900/50">
                <Flame className="inline h-4 w-4 mr-1" /> ¡Delivery Disponible!
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
                El sabor
              </span>
              <br />
              <span className="text-white">que conquista</span>
            </h1>
            
            <p className="text-2xl md:text-3xl font-semibold text-orange-500 italic">
              ¡Crujiente por fuera, irresistible por dentro!
            </p>
            
            <p className="text-xl text-gray-300 max-w-lg">
              Descubre la combinación perfecta entre el crujiente pollo frito y las hamburguesas más jugosas de la ciudad.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-lg px-8 py-6 shadow-lg shadow-red-900/50 hover:shadow-xl hover:shadow-red-900/70 transition-all"
                onClick={onOrderClick}
              >
                Ordenar Ahora
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 border-2 border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-black/50 backdrop-blur-sm"
                onClick={() => document.getElementById('menu').scrollIntoView({ behavior: 'smooth' })}
              >
                Ver Menú
              </Button>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-6 pt-8">
              <div className="flex items-center space-x-2">
                <Truck className="h-5 w-5 text-orange-500" />
                <span className="text-gray-300 font-medium">Entrega en 30 min</span>
              </div>
              <div className="flex items-center space-x-2">
                <Flame className="h-5 w-5 text-red-500" />
                <span className="text-gray-300 font-medium">+1000 clientes felices</span>
              </div>
            </div>

            {/* Social Media Follow */}
            <div className="pt-6 border-t border-gray-800 mt-6">
              <p className="text-gray-400 mb-3 font-medium">¡Síguenos en nuestras redes sociales!</p>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/share/17Guwwc5NV/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg transition-all shadow-lg hover:shadow-xl"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="font-semibold">Facebook</span>
                </a>
                <a
                  href="https://www.instagram.com/mrcrunchfriedchicken"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg transition-all shadow-lg hover:shadow-xl"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="font-semibold">Instagram</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Content - Featured Image with logo */}
          <div className="relative">
            <div className="relative z-10">
              <img
                src="https://customer-assets.emergentagent.com/job_menu-delicioso-2/artifacts/o8djjetr_ChatGPT%20Image%2016%20dic%202025%2C%2013_36_57.png"
                alt="Mr. Crunch 28"
                className="rounded-3xl shadow-2xl shadow-red-900/50 w-full object-contain bg-black border-4 border-orange-500/30 p-4"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-black to-red-950 rounded-2xl shadow-xl shadow-red-900/50 p-6 z-20 border-2 border-orange-500/30">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-full p-3">
                  <Flame className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">+500</p>
                  <p className="text-sm text-gray-400">Pedidos hoy</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
